import React, { useState } from "react";
import { isAxiosError } from "axios";
import { invoiceTemplateNameSchema } from "@common/schemas/domains/invoice/invoice-template.schema";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Field } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { PlainModal, StandardModalHeader } from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import styles from "./InvoiceTemplateNameModal.module.scss";

interface Props {
    show: boolean;
    title: string;
    submitLabel: string;
    initialName?: string;
    isSubmitting: boolean;
    onSubmit: (name: string) => Promise<void>;
    onHide: () => void;
}

function getErrorMessage(error: Error): string {
    if (
        isAxiosError(error) &&
        typeof error.response?.data?.message === "string"
    ) {
        return error.response.data.message;
    }
    return "Couldn't save the template. Try again.";
}

export const InvoiceTemplateNameModal: React.FC<Props> = ({
    show,
    title,
    submitLabel,
    initialName = "",
    isSubmitting,
    onSubmit,
    onHide,
}) => {
    const [name, setName] = useState(initialName);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const parsedName = invoiceTemplateNameSchema.safeParse(name);

    const handleSubmit = async () => {
        if (!parsedName.success) {
            return;
        }
        setErrorMessage(null);
        try {
            await onSubmit(parsedName.data);
            onHide();
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? getErrorMessage(error)
                    : "Couldn't save the template. Try again.",
            );
        }
    };

    return (
        <PlainModal
            show={show}
            onHide={onHide}
            size="md"
            hideCloseButton
            afterHideCallback={() => {
                setName(initialName);
                setErrorMessage(null);
            }}
        >
            <StandardModalHeader>{title}</StandardModalHeader>
            <VStack gap={16} className="mb-4">
                <Field
                    label="Name"
                    invalid={errorMessage !== null}
                    className={styles.nameField}
                >
                    <Input
                        size="xl"
                        maxLength={255}
                        value={name}
                        invalid={errorMessage != null}
                        onChange={(event) => {
                            setName(event.target.value);
                            setErrorMessage(null);
                        }}
                    />
                </Field>
            </VStack>
            <HStack as="footer" gap={12} justify="end">
                <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    onClick={onHide}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                {/* Enter submits (no visible hint) — the caret sits in the
                    name input, hence enableOnFormTags. */}
                <WithHotkey
                    hotKey="enter"
                    hotKeyDisplayPosition="none"
                    hotKeyOptions={{
                        description: submitLabel,
                        enabled: show && !isSubmitting && parsedName.success,
                        enableOnFormTags: true,
                        preventDefault: true,
                    }}
                >
                    <Button
                        type="button"
                        variant="primary"
                        size="md"
                        onClick={() => {
                            void handleSubmit();
                        }}
                        disabled={isSubmitting || !parsedName.success}
                    >
                        {submitLabel}
                    </Button>
                </WithHotkey>
            </HStack>
        </PlainModal>
    );
};
